import React, { useState } from 'react';
import { Outlet, useLocation, useOutlet } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { SalaShell } from './SalaShell';
import { LandingMotifs } from '../landing/LandingMotifs';
import { LandingHeroContent } from '../landing/LandingHeroContent';

const spring = { type: 'spring' as const, stiffness: 380, damping: 34, mass: 0.85 };
const soft = { duration: 0.28, ease: [0.22, 1, 0.36, 1] as const };

const overlayEnter = { opacity: 0, y: 28, scale: 0.978 };
const overlayRest = { opacity: 1, y: 0, scale: 1 };
/** Misma curva que la entrada — salida simétrica (no “corte”). */
const overlayExit = { opacity: 0, y: 28, scale: 0.978 };

function isOverlayPath(pathname: string) {
  return (
    pathname === '/login' ||
    pathname.startsWith('/onboarding') ||
    pathname === '/join'
  );
}

/** Congela el outlet al montar para que AnimatePresence pueda animar la salida. */
function FrozenOutlet() {
  const outlet = useOutlet();
  const [frozen] = useState(() => outlet);
  return <>{frozen}</>;
}

/**
 * Chrome Micelio persistente: motivos + hero fijos.
 * Login / onboarding / join = overlay (entrada y salida con el mismo spring).
 */
export function LandingLayout() {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const isOverlay = isOverlayPath(location.pathname);

  const dimTransition = reduceMotion ? soft : spring;
  const dimAnimate = reduceMotion
    ? { opacity: isOverlay ? 0.22 : 1 }
    : {
        opacity: isOverlay ? 0.22 : 1,
        scale: isOverlay ? 0.985 : 1,
        y: isOverlay ? -8 : 0,
      };

  return (
    <SalaShell variant="landing">
      <div className={`sala-landing-stage${isOverlay ? ' sala-landing-stage--auth' : ''}`}>
        <motion.div
          className="sala-landing-motifs-motion"
          animate={dimAnimate}
          transition={dimTransition}
          aria-hidden
        >
          <LandingMotifs />
        </motion.div>

        <motion.section
          className="sala-landing-hero sala-landing-hero--persistent"
          aria-labelledby="sala-landing-brand"
          aria-hidden={isOverlay}
          animate={dimAnimate}
          transition={dimTransition}
        >
          <LandingHeroContent />
        </motion.section>

        <AnimatePresence>
          {isOverlay ? (
            <motion.div
              key={`overlay-${location.pathname}`}
              className="sala-login-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{
                opacity: 0,
                transition: reduceMotion
                  ? { duration: 0.12 }
                  : { duration: 0.28, ease: [0.22, 1, 0.36, 1], delay: 0.04 },
              }}
              transition={{ duration: reduceMotion ? 0.12 : 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.div
                className="sala-login-overlay__panel-wrap"
                initial={reduceMotion ? { opacity: 0 } : overlayEnter}
                animate={overlayRest}
                exit={reduceMotion ? { opacity: 0 } : overlayExit}
                transition={reduceMotion ? soft : spring}
              >
                <FrozenOutlet />
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        {!isOverlay ? <Outlet /> : null}
      </div>
    </SalaShell>
  );
}
